import { useCallback, useEffect, useRef, useState } from "react";

type Awaitable<T> = T | Promise<T>;

/**
 * Prevents re-entrant async actions from firing more than once until the first
 * execution has finished. This protects against rapid repeated clicks before
 * React has time to re-render a disabled button.
 */
export function useSingleFlight() {
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async <T,>(action: () => Awaitable<T>): Promise<T | undefined> => {
    if (inFlightRef.current) return undefined;

    inFlightRef.current = true;
    if (mountedRef.current) {
      setIsPending(true);
    }

    try {
      return await action();
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setIsPending(false);
      }
    }
  }, []);

  return {
    isPending,
    run,
  };
}
